import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Per-page wrapper. Shape:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ <breadcrumb / kicker>                            │
 *   │ <title>                          [<actions>]     │
 *   │ <subtitle>                                       │
 *   ├──────────────────────────────────────────────────┤
 *   │ children                                         │
 *   └──────────────────────────────────────────────────┘
 *
 * Each route component renders one PageShell so the page chrome stays
 * consistent without us re-implementing it everywhere. Premium UX:
 * generous top padding, narrow content max-width on text-heavy pages
 * (set via the `width` prop), no skeuomorphic header bar.
 */

interface PageShellProps {
  /** Required. Big, semibold, single line. */
  title: ReactNode;
  /** Optional kicker above the title. e.g. "PLATFORM · TENANTS". */
  kicker?: ReactNode;
  /** Optional one-line subtitle below the title. */
  subtitle?: ReactNode;
  /** Action slot — buttons/menus rendered top-right of the header. */
  actions?: ReactNode;
  /** Content max-width. 'narrow' = 720px, 'wide' = 1280px, 'full' = no cap. */
  width?: 'narrow' | 'wide' | 'full';
  children: ReactNode;
}

const WIDTHS: Record<NonNullable<PageShellProps['width']>, number | string> = {
  narrow: 720,
  wide: 1280,
  full: '100%',
};

export function PageShell({
  title,
  kicker,
  subtitle,
  actions,
  width = 'wide',
  children,
}: PageShellProps) {
  const theme = useTheme();

  return (
    <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 4, md: 5 }, maxWidth: WIDTHS[width], mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          {kicker && (
            <Typography
              variant="overline"
              sx={{ color: theme.palette.brand.main, letterSpacing: 0.8 }}
            >
              {kicker}
            </Typography>
          )}
          <Typography
            variant="h1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: 26, md: 32 },
              fontWeight: 600,
              letterSpacing: -0.4,
              lineHeight: 1.15,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {actions && (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Stack>

      {/* Body */}
      <Box>{children}</Box>
    </Box>
  );
}
